import React from 'react';
import styled, { css } from 'styled-components';
import { colors, spacing, typography, radius, shadows, transitions } from '../../tokens';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'base' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  loading?: boolean;
  icon: React.ReactNode; // Required icon
  iconPosition?: 'left' | 'right';
  children?: React.ReactNode;
}

const sizes = {
  sm: css`
    height: 28px;
    padding: 0 ${spacing[2]};
    font-size: ${typography.fontSizes['xs+']};
    border-radius: ${radius.sm};
  `,
  base: css`
    height: 28px;
    padding: 0 ${spacing[2]};
    font-size: ${typography.fontSizes['xs+']};
    border-radius: ${radius.base};
  `,
  lg: css`
    height: 28px;
    padding: 0 ${spacing[3]};
    font-size: ${typography.fontSizes['xs+']};
    border-radius: ${radius.base};
  `,
};

const variants = {
  primary: css`
    background: ${colors.button.primary.background};
    color: ${colors.button.primary.text};
    border: 1px solid transparent;
    transition: box-shadow 0.2s ease;
    position: relative;
    
    &:hover:not(:disabled) {
      box-shadow: inset 0 0 0 999px rgba(0, 0, 0, 0.08);
    }
    
    &:active:not(:disabled) {
      box-shadow: inset 0 0 0 999px rgba(0, 0, 0, 0.15);
    }

    &:disabled {
      background: ${colors.palette.gray[200]};
      color: ${colors.palette.gray[400]};
      cursor: not-allowed;
      opacity: 1;
    }
  `,
  secondary: css`
    background: ${colors.button.secondary.background};
    color: ${colors.button.secondary.text};
    border: 1px solid ${colors.button.secondary.border};
    transition: border-color 0.2s ease, background-color 0.2s ease;
    
    &:hover:not(:disabled) {
      border-color: ${colors.border.hover};
      background: ${colors.background.hover};
    }
    
    &:active:not(:disabled) {
      background: ${colors.background.active};
    }

    &:disabled {
      background: ${colors.palette.gray[50]};
      color: ${colors.palette.gray[400]};
      border-color: ${colors.palette.gray[200]};
      cursor: not-allowed;
      opacity: 1;
    }
  `,
  ghost: css`
    background: ${colors.button.ghost.background};
    color: ${colors.button.ghost.text};
    border: 1px solid transparent;
    transition: background-color 0.2s ease;
    
    &:hover:not(:disabled) {
      background: ${colors.background.hover};
    }
    
    &:active:not(:disabled) {
      background: ${colors.background.active};
    }

    &:disabled {
      background: transparent;
      color: ${colors.palette.gray[400]};
      cursor: not-allowed;
      opacity: 1;
    }
  `,
  danger: css`
    background: ${colors.button.danger.background};
    color: ${colors.button.danger.text};
    border: 1px solid transparent;
    transition: box-shadow 0.2s ease;
    position: relative;
    
    &:hover:not(:disabled) {
      box-shadow: inset 0 0 0 999px rgba(0, 0, 0, 0.08);
    }
    
    &:active:not(:disabled) {
      box-shadow: inset 0 0 0 999px rgba(0, 0, 0, 0.15);
    }

    &:disabled {
      background: ${colors.palette.gray[200]};
      color: ${colors.palette.gray[400]};
      cursor: not-allowed;
      opacity: 1;
    }
  `,
};

const StyledButton = styled.button<{
  $variant: ButtonVariant;
  $size: ButtonSize;
  $fullWidth?: boolean;
  $loading?: boolean;
  $hasIcon?: boolean;
  $iconOnly?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing[2]};
  border: none;
  border-radius: ${radius.base};
  font-weight: ${typography.fontWeights.medium};
  line-height: 1;
  cursor: pointer;
  transition: ${transitions.button.base};
  outline: none;
  position: relative;
  
  ${props => sizes[props.$size]}
  ${props => variants[props.$variant]}
  
  ${props => props.$fullWidth && css`
    width: 100%;
  `}
  
  ${props => props.$iconOnly && css`
    padding: 0;
    width: ${props.$size === 'sm' ? '24px' : props.$size === 'lg' ? '36px' : '32px'};
  `}
  
  &:focus:not(:disabled) {
    box-shadow: 0 0 0 3px ${colors.brand.primarySurface};
  }
  
  ${props => props.$loading && css`
    color: transparent;
    pointer-events: none;
    
    &::after {
      content: '';
      position: absolute;
      width: 16px;
      height: 16px;
      top: 50%;
      left: 50%;
      margin-left: -8px;
      margin-top: -8px;
      border: 2px solid currentColor;
      border-radius: 50%;
      border-color: ${props.$variant === 'primary' || props.$variant === 'danger' 
        ? colors.text.inverse 
        : colors.text.primary};
      border-right-color: transparent;
      animation: spin 0.6s linear infinite;
    }
    
    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }
  `}
`;

const IconWrapper = styled.span<{ $position?: 'left' | 'right'; $size?: ButtonSize }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  order: ${props => props.$position === 'right' ? 1 : 0};
  
  svg {
    width: ${props => props.$size === 'sm' ? '12px' : props.$size === 'lg' ? '16px' : '14px'};
    height: ${props => props.$size === 'sm' ? '12px' : props.$size === 'lg' ? '16px' : '14px'};
  }
`;

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'base',
  fullWidth = false,
  loading = false,
  icon,
  iconPosition = 'left',
  children,
  disabled,
  ...props
}) => {
  const hasIcon = Boolean(icon);
  const iconOnly = hasIcon && !children;
  
  return (
    <StyledButton
      $variant={variant}
      $size={size}
      $fullWidth={fullWidth}
      $loading={loading}
      $hasIcon={hasIcon}
      $iconOnly={iconOnly}
      disabled={disabled || loading}
      {...props}
    >
      {icon && (
        <IconWrapper $position={iconPosition} $size={size}>
          {icon}
        </IconWrapper>
      )}
      {children}
    </StyledButton>
  );
}; 